import { Container, Graphics, Point } from 'pixi.js';
import { MonoBehavior } from '../core/monoBehavior';
import { addToLevel } from '../core/Playable2DRootContainer';
import { GameEvent } from '../events/gameEvent';
import { EventName } from '../events/eventsName';
import { SoundManager } from '../core/soundManager';
import { Layout } from '../LayoutManager';
import gsap, { Power1 } from 'gsap';
import { CARD_MOVE_TIME } from 'constants/move';
import { TranslationText } from './TranslationText';
import { autocompleteStyle } from './fileStyle';
import { Store } from 'game/store/gameStore';
import { addHoverEffect } from './hover';
import { Displays, DESKTOP_SIDEBAR_WIDTH, BUTTONS_BOTTOM_MARGIN_DESKTOP } from 'constants/layout';

export class AutocompleteButton extends MonoBehavior {
    container: Container;
    contentContainer: Container;
    settingsOpen = false;
    buttonGraphics: Graphics;
    translationText: TranslationText;
    autocompletePossible = false;
    isButtonActive = false;
    autocompleteSound?: Howl;
    soundPlayed = false;
    status = 'no' as 'auto' | 'clicked' | 'no';
    autocompleteTriggered = false;

    constructor() {
        super();
        this.autocomplete = this.autocomplete.bind(this);
        this.refreshState = this.refreshState.bind(this);
        this.onOpenSettings = this.onOpenSettings.bind(this);
        this.onCloseSettings = this.onCloseSettings.bind(this);
        this.onAutocompleteFinish = this.onAutocompleteFinish.bind(this);
        this.container = new Container();
        this.contentContainer = new Container();
        this.container.addChild(this.contentContainer);
        this.container.zIndex = 10;
        this.container.sortableChildren = true;
        this.buttonGraphics = new Graphics();
        this.buttonGraphics.zIndex = 1;
        this.translationText = new TranslationText('autocomplete', autocompleteStyle);
        this.translationText.text.zIndex = 10;
        this.translationText.setAnchor(0.5);
        this.contentContainer.addChild(this.translationText.text);

        this.contentContainer.addChild(this.buttonGraphics);
        this.container.visible = false;
        GameEvent.add(EventName.OPEN_SETTINGS, this.onOpenSettings);
        GameEvent.add(EventName.HIDE_SETTINGS, this.onCloseSettings);
        GameEvent.add(EventName.TOGGLE_AUTOCOMPLETE, this.refreshState);
        GameEvent.add(EventName.AUTOCOMPLETE_COMPLETE, this.onAutocompleteFinish);
    }
    onAutocompleteFinish() {
        if (this.autocompleteSound) {
            this.autocompleteSound.on('fade', () => {
                this.autocompleteSound?.stop();
            });
            this.autocompleteSound.fade(1, 0, 1000);
        }
    }

    start(): void {
        addToLevel(this.container);
        this.refreshState();
        this.container.on('pointerdown', this.autocomplete);
        addHoverEffect(this.container);
    }
    autocomplete() {
        this.status = Store.autocomplete ? 'auto' : 'clicked';
        this.autocompleteTriggered = true;
        this.container.visible = false;

        if (Store.isKlondike) this.autocompleteSound = SoundManager.play('autocompleteRunSFX', { loop: true }).instance;
        if (Store.isFreecell) this.autocompleteSound = SoundManager.play('autocompleteRunSFX', { loop: true }).instance;

        GameEvent.emit(EventName.RUN_AUTOCOMPLETE);
        this.autocompletePossible = false;

        this.refreshState();
    }
    drawButton(width: number, height: number) {
        const border = 2;

        this.buttonGraphics.clear();
        this.buttonGraphics.roundRect(-width / 2, -height / 2, width, height, height / 2);
        this.buttonGraphics.fill(0x1f1f1f);
        this.buttonGraphics.stroke({ width: border, color: 0xffffff });
        this.buttonGraphics.alpha = 0.3;
    }
    refreshState() {
        if (this.autocompleteTriggered) return;
        if (this.autocompletePossible && Store.autocomplete) {
            this.autocomplete();
            return;
        }
        //run autocomplete
        const show = this.autocompletePossible;

        if (show && !this.soundPlayed) {
            this.soundPlayed = true;
            SoundManager.play('autocompleteShowSFX');
        }
        this.container.visible = show;
        this.isButtonActive = show;
        this.container.eventMode = show ? 'static' : 'none';
        this.container.cursor = show ? 'pointer' : undefined;
    }

    resize(width: number, height: number): void {
        const buttonHeight = Layout.getButtonSize();

        const display = Layout.getDisplay();
        const position = new Point();
        const content = new Point();
        let buttonWidth = 100;
        let textWidth = 100;
        switch (display) {
            case Displays.MOBILE:
                this.translationText.text.style.fontSize = 16;
                textWidth = this.translationText.getSize().width;

                buttonWidth = textWidth + 42;
                this.drawButton(buttonWidth, buttonHeight);
                position.set(0, height / 2 - 108);

                break;
            case Displays.TABLET:
                this.translationText.text.style.fontSize = 19;
                textWidth = this.translationText.getSize().width;

                buttonWidth = textWidth + 52;
                this.drawButton(buttonWidth, buttonHeight);
                position.set(0, height / 2 - 140);

                break;
            case Displays.DESKTOP:
                this.translationText.text.style.fontSize = 19;
                textWidth = this.translationText.getSize().width;

                buttonWidth = textWidth + 60;
                this.drawButton(buttonWidth, buttonHeight);
                position.set(
                    width / 2 - buttonWidth / 2 - DESKTOP_SIDEBAR_WIDTH - 50,
                    height / 2 - buttonHeight / 2 - BUTTONS_BOTTOM_MARGIN_DESKTOP
                );

                break;
        }
        this.container.position = position;
        this.contentContainer.position = content;
    }
    onOpenSettings() {
        const display = Layout.getDisplay();
        if (display === Displays.DESKTOP) {
            gsap.to(this.contentContainer.position, {
                x: -260,
                ease: Power1.easeOut,
                duration: CARD_MOVE_TIME,
            });
        }
    }
    onCloseSettings() {
        const textWidth = this.translationText.getSize().width;

        const display = Layout.getDisplay();

        if (display === Displays.DESKTOP) {
            gsap.to(this.contentContainer.position, {
                x: 0,
                ease: Power1.easeOut,
                duration: CARD_MOVE_TIME,
            });
        }
    }

    changeVisibility(autoCompleteActive: boolean) {
        this.autocompletePossible = autoCompleteActive;
        this.refreshState();
    }
    reset() {
        this.autocompleteTriggered = false;
        this.soundPlayed = false;
        this.status = 'no';
    }
}
